/**
 * LeftMenu — 워크스페이스 좌측 사이드바
 *
 * 구조 (위 → 아래):
 *   1. LeftMenuHeader (full width): 프로필 + 워크스페이스 전환
 *   2. Body (2-column):
 *      - 좌측 narrow: LeftMenuModuleRail (채널/게시판/캘린더 세로 스위처)
 *      - 우측: 선택된 activeModule 의 사이드바 콘텐츠 (LeftMenuRooms/Board/Calendar/Files)
 *
 * 이전: 좌측 narrow AppRail + 우측 LeftMenu 사이드바의 두 컬럼 구조였으나,
 *      AppRail 의 모듈 스위처가 워크스페이스보다 상위처럼 보여 폐기. 통합.
 *      이후 하단 NEXTPOD 푸터(AppMenuButton)도 제거 — 진입 빈도 낮아 사이드바 공간 차지가 아까움.
 */
import { useEffect, useState } from 'react';
import { useLocation } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import Page from '../../const/Page';
import { LeftMenuHeader } from './LeftMenuHeader';
import LeftMenuRooms from '../rooms/LeftMenuRooms';
import LeftMenuSoribot from '../soribots/LeftMenuSoribot';
import LeftMenuBoard from '../board/LeftMenuBoard';
import LeftMenuCalendar from '../calendar/LeftMenuCalendar';
import LeftMenuFiles from '../files/LeftMenuFiles';
import LeftMenuModuleRail from './LeftMenuModuleRail';
import RejoinRoomCallModal from './RejoinRoomCallModal';

const LeftMenu: React.FC = () => {
  const location = useLocation();
  const { activeModule } = useAppUIContext();

  // 초기값을 현재 경로에 따라 설정 (첫 렌더링부터 올바른 메뉴 표시)
  const getMenuTypeFromPath = (path: string): 'room-menu' | 'workspace-admin' | 'soribot-menu' | 'hidden' => {
    if (!path) return 'room-menu';
    if (path.startsWith('/ws-admin')) return 'workspace-admin';
    if (path === Page.publicSharePage || path.startsWith(Page.publicSharePage + '/') || path === Page.publicBotPage || path.startsWith(Page.publicBotPage + '/')) return 'hidden';
    if (path.startsWith(Page.soribot)) return 'soribot-menu';
    return 'room-menu';
  };

  const [menuType, setMenuType] = useState<'room-menu' | 'workspace-admin' | 'soribot-menu' | 'hidden'>(
    () => getMenuTypeFromPath(location.pathname),
  );

  useEffect(() => {
    updateMenuType(location.pathname);
  }, [location.pathname]);

  function updateMenuType(path: string) {
    if (path) {
      if (path.startsWith('/ws-admin')) {
        setMenuType('workspace-admin');
      } else if (path === Page.publicSharePage || path.startsWith(Page.publicSharePage + '/') || path === Page.publicBotPage || path.startsWith(Page.publicBotPage + '/')) {
        setMenuType('hidden');
      } else if (path.startsWith(Page.soribot)) {
        setMenuType('soribot-menu');
      } else {
        setMenuType('room-menu');
      }
    }
  }

  if (menuType === 'hidden') return null;

  // activeModule에 따라 우측 콘텐츠 교체. soribot 메뉴는 우선 표시 (activeModule 무관)
  const renderSidebarContent = () => {
    if (menuType === 'soribot-menu') return <LeftMenuSoribot />;
    switch (activeModule) {
      case 'board':    return <LeftMenuBoard />;
      case 'calendar': return <LeftMenuCalendar />;
      case 'files':    return <LeftMenuFiles />;
      case 'chat':
      default:         return <LeftMenuRooms />;
    }
  };

  // soribot 메뉴는 자체 사이드바 풀 사용. 모바일도 Rail 표시 — 하단 MobileTabBar 폐기 (채널 헤더 탭과 혼동 방지)
  const showModuleRail = menuType !== 'soribot-menu';

  return (
    <div className="left-menu h-full flex flex-col">
      {menuType !== 'soribot-menu' && <LeftMenuHeader />}

      {/* 본문 — soribot 은 단일 컬럼, 일반 모듈은 2-column (rail | content) */}
      <div className="flex flex-1 min-h-0">
        {showModuleRail && <LeftMenuModuleRail />}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {renderSidebarContent()}
        </div>
      </div>

      <RejoinRoomCallModal />
    </div>
  );
};

export default LeftMenu;
